import { useEffect, useState, useCallback, useMemo } from "react";
import { http, fmtEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import {
  TrendingUp,
  RefreshCw,
  Trophy,
  TrendingDown,
  Receipt,
  Clock,
  Users,
  FileDown,
} from "lucide-react";

const COLORS = [
  "#2563EB", "#16A34A", "#F59E0B", "#DC2626", "#A855F7",
  "#0EA5E9", "#EC4899", "#65A30D", "#F97316", "#06B6D4",
];

function Stat({ icon: Icon, label, value, sub, color = "blue" }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-md grid place-items-center text-white bg-${color}-600`}>
          <Icon size={20} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
          <div className="text-xl font-heading font-bold truncate">{value}</div>
          {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

export default function StatisticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [saleClass, setSaleClass] = useState("both");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) {
        const dt = new Date(dateTo);
        dt.setHours(23, 59, 59, 999);
        params.date_to = dt.toISOString();
      }
      if (saleClass !== "both") params.sale_class = saleClass;
      const r = await http.get("/stats/sales", { params });
      setData(r.data);
    } catch (e) {
      // Feb 2026: previously we swallowed all errors silently which made
      // «τα στατιστικά δεν δουλεύουν» impossible to debug. Now surface a
      // toast so the operator sees the exact failure reason.
      const detail = e?.response?.data?.detail || e?.message || "Αποτυχία φόρτωσης στατιστικών";
      toast.error(detail);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, saleClass]);

  useEffect(() => {
    const t = setTimeout(fetchStats, 0);
    return () => clearTimeout(t);
  }, [fetchStats]);

  const onExportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      // Text-based PDF (was a 24 MB html2canvas screenshot before, with no
      // Greek glyphs in helvetica). Now: jsPDF + jspdf-autotable + a
      // NotoSans Greek subset (~51 KB) embedded at runtime → ~150 KB total,
      // selectable Greek text, sharp rows even for the bottom tables.
      const [{ default: jsPDF }, autoTableMod] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
      const autoTable = autoTableMod.default || autoTableMod.autoTable;

      // Lazy-fetch the font on first use and cache on window so subsequent
      // exports skip the network. Stored as base64 (jsPDF's VFS format).
      let fontB64 = window.__notoSansGreekB64;
      if (!fontB64) {
        const resp = await fetch("/fonts/NotoSans-Greek-Subset.ttf");
        const buf = await resp.arrayBuffer();
        let bin = "";
        const bytes = new Uint8Array(buf);
        for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
        fontB64 = btoa(bin);
        window.__notoSansGreekB64 = fontB64;
      }

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      pdf.addFileToVFS("NotoSansGreek.ttf", fontB64);
      pdf.addFont("NotoSansGreek.ttf", "NotoSansGreek", "normal");
      pdf.setFont("NotoSansGreek", "normal");

      const pageW = pdf.internal.pageSize.getWidth();
      const M = 12;
      const stamp = new Date().toLocaleString("el-GR");
      const range =
        [
          dateFrom ? `Από ${dateFrom}` : null,
          dateTo ? `Έως ${dateTo}` : null,
          saleClass !== "both" ? `Κατηγορία: ${saleClass}` : null,
        ]
          .filter(Boolean)
          .join("  ·  ") || "Όλες οι ημερομηνίες";

      // ---- Header ---------------------------------------------------------
      pdf.setFontSize(16);
      pdf.text("Στατιστικά Πωλήσεων — Ponte Pos", M, 14);
      pdf.setFontSize(9.5);
      pdf.setTextColor(80);
      pdf.text(range, M, 20);
      pdf.text(`Εκτυπώθηκε: ${stamp}`, pageW - M, 20, { align: "right" });
      pdf.setTextColor(0);

      const s = data.summary || {};
      const fmt2 = (n) => Number(n || 0).toFixed(2).replace(".", ",");

      // ---- KPI summary table ---------------------------------------------
      autoTable(pdf, {
        startY: 25,
        head: [["Σύνοψη", "Τιμή"]],
        body: [
          ["Σύνολο τζίρου (€)", fmt2(s.total_amount)],
          ["Αποδείξεις", String(s.total_receipts || 0)],
          ["Πελάτες", String(s.unique_customers || 0)],
          ["Μέσος όρος απόδειξης (€)", fmt2(s.avg_receipt)],
          ["Είδη πωληθέντα (qty)", String(s.total_qty || 0)],
        ],
        styles: { font: "NotoSansGreek", fontSize: 10, cellPadding: 1.6 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 10 },
        columnStyles: { 1: { halign: "right", cellWidth: 45 } },
        theme: "grid",
        margin: { left: M, right: M },
      });

      const tableSection = (title, head, body) => {
        if (!body || body.length === 0) return;
        const startY = (pdf.lastAutoTable?.finalY || 28) + 7;
        pdf.setFontSize(11);
        pdf.text(title, M, startY);
        autoTable(pdf, {
          startY: startY + 2,
          head: [head],
          body,
          styles: { font: "NotoSansGreek", fontSize: 9, cellPadding: 1.4, overflow: "linebreak" },
          headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          theme: "grid",
          margin: { left: M, right: M },
        });
      };

      tableSection(
        "Ανά κατηγορία",
        ["Κατηγορία", "Τζίρος €", "Ποσότητα", "Αποδ."],
        (data.by_category || []).map((r) => [r.category, fmt2(r.amount), r.qty, r.receipts || ""]),
      );
      tableSection(
        "Ανά τμήμα ΦΠΑ",
        ["Τμήμα ΦΠΑ", "Τζίρος €", "Ποσότητα"],
        (data.by_vat || []).map((r) => [`VAT#${r.vat_rate}`, fmt2(r.amount), r.qty]),
      );
      tableSection(
        "Πωλήσεις ανά ώρα της ημέρας",
        ["Ώρα", "Τζίρος €", "Αποδείξεις"],
        (data.by_hour || []).filter((h) => h.count > 0).map((h) => [`${h.hour}:00`, fmt2(h.amount), h.count]),
      );
      tableSection(
        "Top 10 σε τζίρο",
        ["#", "Είδος", "Τζίρος €"],
        (data.top_products_amount || []).map((p, i) => [i + 1, p.description, fmt2(p.amount)]),
      );
      tableSection(
        "Top 10 σε ποσότητα",
        ["#", "Είδος", "Ποσότητα"],
        (data.top_products_qty || []).map((p, i) => [i + 1, p.description, p.qty]),
      );
      tableSection(
        "Χειρότερη κίνηση (10)",
        ["#", "Είδος", "Ποσότητα"],
        (data.worst_products_qty || []).map((p, i) => [i + 1, p.description, p.qty]),
      );

      // Footer page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(
          `Σελίδα ${i} / ${pageCount}`,
          pageW - M,
          pdf.internal.pageSize.getHeight() - 6,
          { align: "right" },
        );
        pdf.setTextColor(0);
      }

      const dt = new Date().toISOString().slice(0, 10);
      const filename = `statistics-${dt}.pdf`;
      // Feb 2026 — send to Companion agent (C:\FText\reports\)
      // instead of pdf.save() so kiosk-mode operators actually get the
      // file. jsPDF returns an ArrayBuffer via `output('arraybuffer')`.
      try {
        const buf = pdf.output("arraybuffer");
        const bytes = new Uint8Array(buf);
        let bin = "";
        const CHUNK = 0x8000;
        for (let i = 0; i < bytes.length; i += CHUNK) {
          bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        const b64 = btoa(bin);
        const compResp = await fetch("http://127.0.0.1:9100/save-file", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folder: "reports",
            filename,
            content_b64: b64,
          }),
        });
        if (!compResp.ok) throw new Error(`Companion HTTP ${compResp.status}`);
        const j = await compResp.json();
        if (!j.ok) throw new Error(j.error || "Companion save failed");
        toast.success(`Αποθηκεύτηκε: ${j.path}`);
      } catch (err) {
        toast.error(
          err?.message?.includes("Companion") || err?.message?.includes("Failed to fetch")
            ? "Ο Companion agent δεν αποκρίνεται (πόρτα 9100)"
            : "Αποτυχία αποθήκευσης PDF",
        );
      }
    } catch (e) {
      toast.error("Αποτυχία εξαγωγής PDF");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};

  const byHourFiltered = useMemo(
    () => (data?.by_hour || []).filter((h) => h.count > 0),
    [data]
  );

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Από</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 w-44"
            data-testid="stats-from"
          />
        </div>
        <div>
          <Label className="text-xs">Έως</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 w-44"
            data-testid="stats-to"
          />
        </div>
        <div>
          <Label className="text-xs">Κατηγορία πώλησης</Label>
          <Select value={saleClass} onValueChange={setSaleClass}>
            <SelectTrigger className="h-9 w-52" data-testid="stats-saleclass">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Όλες (AA + BB + legacy)</SelectItem>
              <SelectItem value="AA">AA – Ταμειακή</SelectItem>
              <SelectItem value="BB">BB – Δισκέτα</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={fetchStats}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 h-9"
          data-testid="stats-refresh"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Ανανέωση
        </Button>
        <Button
          onClick={onExportPDF}
          disabled={exporting || loading}
          variant="outline"
          className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="stats-export-pdf"
        >
          <FileDown size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
          {exporting ? "Δημιουργία PDF..." : "Εξαγωγή PDF"}
        </Button>
      </Card>

      {/* PDF export area */}
      <div className="space-y-4 bg-white">

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="stats-summary">
        <Stat icon={TrendingUp} label="Σύνολο Τζίρου" value={fmtEUR(summary.total_amount || 0)} color="emerald" />
        <Stat icon={Receipt} label="Αποδείξεις" value={summary.receipts_count || 0} color="blue" />
        <Stat icon={Users} label="Είδη Πωληθέντα" value={Number.isInteger(Number(summary.items_total || 0)) ? (summary.items_total || 0) : Number(summary.items_total).toFixed(2)} color="violet" />
        <Stat icon={TrendingUp} label="Μ.Ο. Απόδειξης" value={fmtEUR(summary.avg_receipt || 0)} color="amber" />
        <Stat icon={Users} label="Τραπέζια" value={summary.tables_served || 0} sub="που σερβιρίστηκαν" color="red" />
      </div>

      {/* Sales per day - line chart */}
      <Card className="p-4">
        <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
          <TrendingUp size={16} /> Πωλήσεις ανά ημέρα
        </h4>
        {(data?.by_day || []).length === 0 ? (
          <div className="h-48 grid place-items-center text-slate-400 text-sm">Καμία πώληση</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => (name === "amount" ? fmtEUR(v) : v)} />
              <Legend />
              <Line type="monotone" dataKey="amount" name="Τζίρος (€)" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="count" name="Αποδείξεις" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sales per category */}
        <Card className="p-4">
          <h4 className="font-heading font-semibold text-base mb-3">Ανά κατηγορία</h4>
          {(data?.by_category || []).length === 0 ? (
            <div className="h-48 grid place-items-center text-slate-400 text-sm">—</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={data.by_category}
                  dataKey="amount"
                  nameKey="category"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                  label={(e) => `${e.category}: ${fmtEUR(e.amount)}`}
                >
                  {data.by_category.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Sales per VAT */}
        <Card className="p-4">
          <h4 className="font-heading font-semibold text-base mb-3">Ανά τμήμα ΦΠΑ</h4>
          {(data?.by_vat || []).length === 0 ? (
            <div className="h-48 grid place-items-center text-slate-400 text-sm">—</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.by_vat}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="vat_rate" tickFormatter={(v) => `VAT#${v}`} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name) => (name === "amount" ? fmtEUR(v) : v)} />
                <Legend />
                <Bar dataKey="amount" name="Τζίρος (€)" fill="#2563EB" />
                <Bar dataKey="qty" name="Ποσότητα" fill="#16A34A" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Sales per hour */}
      <Card className="p-4">
        <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
          <Clock size={16} /> Πωλήσεις ανά ώρα της ημέρας
        </h4>
        {byHourFiltered.length === 0 ? (
          <div className="h-48 grid place-items-center text-slate-400 text-sm">Καμία πώληση</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_hour}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickFormatter={(h) => `${h}:00`} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => (name === "amount" ? fmtEUR(v) : v)} labelFormatter={(h) => `${h}:00`} />
              <Legend />
              <Bar dataKey="amount" name="Τζίρος (€)" fill="#A855F7" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top by amount */}
        <Card className="p-4">
          <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" /> Top 10 σε τζίρο
          </h4>
          <ol className="space-y-1.5">
            {(data?.top_products_amount || []).length === 0 && (
              <li className="text-slate-400 text-sm">—</li>
            )}
            {(data?.top_products_amount || []).map((p, i) => (
              <li key={i} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                <span className="truncate">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{i + 1}.</span>
                  {p.description}
                </span>
                <span className="font-mono font-bold text-emerald-700 shrink-0 ml-2">{fmtEUR(p.amount)}</span>
              </li>
            ))}
          </ol>
        </Card>

        {/* Top by qty */}
        <Card className="p-4">
          <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <Trophy size={16} className="text-blue-500" /> Top 10 σε ποσότητα
          </h4>
          <ol className="space-y-1.5">
            {(data?.top_products_qty || []).length === 0 && (
              <li className="text-slate-400 text-sm">—</li>
            )}
            {(data?.top_products_qty || []).map((p, i) => (
              <li key={i} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                <span className="truncate">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{i + 1}.</span>
                  {p.description}
                </span>
                <span className="font-mono font-bold text-blue-700 shrink-0 ml-2">{Number.isInteger(Number(p.qty)) ? p.qty : Number(p.qty).toFixed(2)}</span>
              </li>
            ))}
          </ol>
        </Card>

        {/* Worst */}
        <Card className="p-4">
          <h4 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
            <TrendingDown size={16} className="text-red-500" /> Χειρότερη κίνηση
          </h4>
          <ol className="space-y-1.5">
            {(data?.worst_products_qty || []).length === 0 && (
              <li className="text-slate-400 text-sm">—</li>
            )}
            {(data?.worst_products_qty || []).map((p, i) => (
              <li key={i} className="flex justify-between text-sm border-b border-slate-100 pb-1">
                <span className="truncate">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{i + 1}.</span>
                  {p.description}
                </span>
                <span className="font-mono font-bold text-red-700 shrink-0 ml-2">{Number.isInteger(Number(p.qty)) ? p.qty : Number(p.qty).toFixed(2)}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      </div>
    </div>
  );
}
